/*
<summary>
   This functional component "AddEditToolManagement" defines the props like initial values
   & functions for the Add/Edit Tool Management child form component.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import InspectionCycleEnum from '../../../constants/enums/inspection-cycle-enum';
import InspectionFormatEnum from '../../../constants/enums/inspection-format-enum';
import InspectionStatusEnum from '../../../constants/enums/inspection-status-enum';
import ToolTypeEnum from '../../../constants/enums/tool-type-enum';
import UsageStatusEnum from '../../../constants/enums/usage-status-enum';
import { useStore } from '../../../contexts/store-provider';
import { getFactoryIdFromLocalStorage, getTenantIdFromLocalStorage, getUserId } from '../../../helpers/localstorage-helper';
import {
  IAddToolManagement,
  IToolCrimpMasterDetailRequest,
  IToolUciCheckitemInfoDetailRequest,
  IToolUciFileDetailRequest,
  IUpdateToolCrimpMasterDetailRequest,
  IUpdateToolManagement,
  IUpdateToolUciCheckitemInfoDetailRequest,
  IUpdateToolUciFileDetailRequest,
} from '../../../models/forms/IAddUpdateToolManagement';
import { IToolCrimpRow, IToolInspectionTab } from '../../../models/forms/IToolInspection';
import { IFormModalProps } from '../../../models/forms/IForm';
import { IToolManagementVM } from '../../../models/response/IToolManagementResponse';
import { formatMessage } from '../../../translations/format-message';
import AddEditToolManagementForm from '../forms/add-edit-tool-management-form';
import { errorMessage } from '../../../constants/error-constants';
import { parseProcessUsedToOptions, stringifyProcessUsedNames } from '../process-used-helper';

const PLEASE_SELECT_NUMBER_FIELDS: (keyof IToolManagementVM)[] = [
  'InspectionStatus',
  'CalibrationStatus',
  'InspectionCycle',
];

/**
* This function is used to clear the "please_select" placeholder values from the form
* before submitting.
* @param values: Tool Management Form Values
*/
const clearPleaseSelectSentinels = (values: IToolManagementVM): IToolManagementVM => {
  const cleaned: any = { ...values };
  PLEASE_SELECT_NUMBER_FIELDS.forEach((field) => {
    if (Number(cleaned[field]) === -1) {
      cleaned[field] = null;
    }
  });
  return cleaned;
};

/** These are the date fields that need to be sent to the API as plain "YYYY-MM-DD" strings. */
const DATE_FIELDS: (keyof IToolManagementVM)[] = [
  'CalibrationExpiryDate',
  'PurchaseDate',
  'DisposalDate',
  'LoanDate',
  'PlannedReturnDate',
];

/**
* This function is used to format the date fields to "YYYY-MM-DD" before submitting.
* @param values: Tool Management Form Values
*/
const mapDateFieldsForSubmit = (values: IToolManagementVM): IToolManagementVM => {
  const mapped: any = { ...values };
  DATE_FIELDS.forEach((field) => {
    const value = mapped[field];
    if (value) {
      const date = new Date(value);
      const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      mapped[field] = localDate.toISOString().split('T')[0];
    }
  });
  return mapped;
};

/** These are the actual Tool Management request fields, used to build the Add/Update payload. */
const BASE_TOOL_MANAGEMENT_FIELDS: (keyof IToolManagementVM)[] = [
  'ToolManagementNo',
  'ToolName',
  'OwnerFactoryId',
  'ToolManufacturer',
  'ToolModel',
  'SerialNo',
  'EquipmentCategory',
  'ProcessUsed',
  'UsageStatus',
  'InspectionStatus',
  'InspectionFormat',
  'InspectionInterval',
  'InspectionCycle',
  'CalibrationStatus',
  'CalibrationExpiryDate',
  'NoOfDaysUntilWarning',
  'PurchaseDate',
  'DisposalDate',
  'Location',
  'ManagingDepartment',
  'ResponsibleManager',
  'FixedAssetNo',
  'TagId',
  'TeachmebizUrl',
  'Remarks',
  'IsActive',
  'LoanFactoryId',
  'LoanDate',
  'LoanRecord',
  'PlannedReturnDate',
  'PartsManufacturer',
  'ReelModel',
  'LooseTerminalModel',
  'ToolType',
];


const NUMERIC_ENUM_FIELDS: (keyof IToolManagementVM)[] = [
  'UsageStatus',
  'InspectionStatus',
  'InspectionFormat',
  'InspectionInterval',
  'InspectionCycle',
  'CalibrationStatus',
  'NoOfDaysUntilWarning',
  'LoanRecord',
];

// No. of Days Until Warning is a nullable field in the UI, but the API always expects a
// number, defaulting to 0 when the operator leaves it blank.
const NUMERIC_FIELDS_DEFAULT_ZERO: (keyof IToolManagementVM)[] = ['NoOfDaysUntilWarning'];

/**
* This function is used to pick only the request fields out of the form values before
* submitting, coercing the enum-backed select/radio fields to actual numbers.
* @param values: Tool Management Form Values
*/
const pickBaseToolManagementFields = (values: IToolManagementVM): Record<string, unknown> => {
  const picked: Record<string, unknown> = {};
  BASE_TOOL_MANAGEMENT_FIELDS.forEach((field) => {
    picked[field] = (values as any)[field];
  });
  NUMERIC_ENUM_FIELDS.forEach((field) => {
    const value = picked[field];
    const isBlank = value === null || value === undefined || value === '';
    if (isBlank) {
      picked[field] = NUMERIC_FIELDS_DEFAULT_ZERO.includes(field) ? 0 : null;
    } else {
      picked[field] = Number(value);
    }
  });
  return picked;
};

/**
* This function is used to convert a data URL (e.g. a locally previewed image) into a
* File instance so it can be uploaded to the temp files API.
* @param dataUrl: Data URL
* @param fileName: File Name to assign
*/
const dataUrlToFile = async (dataUrl: string, fileName: string): Promise<File> => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type });
};

/**
* This function is used to flatten an inspection tab's check items (from its Headers, falling
* back to its flat CheckItems list) into a single list along with each item's header name and
* the header's display order (its 1-based position in the Headers array — the same order the
* Move Up/Down controls in the Check Items panel produce), so the API can persist the header
* order the user arranged instead of losing it.
* @param tab: Inspection Tab
*/
const flattenTabCheckItems = (tab: IToolInspectionTab) => {
  if (tab.Headers && tab.Headers.length > 0) {
    return tab.Headers.flatMap((header, headerIndex) =>
      (header.Items || []).map((item) => ({
        item,
        header: header.Title || item.HeaderTitle || 'Default',
        headerOrder: headerIndex + 1,
      }))
    );
  }
  const headerOrderByTitle: Record<string, number> = {};
  return (tab.CheckItems || []).map((item) => {
    const header = item.HeaderTitle || 'Default';
    if (!(header in headerOrderByTitle)) {
      headerOrderByTitle[header] = Object.keys(headerOrderByTitle).length + 1;
    }
    return { item, header, headerOrder: headerOrderByTitle[header] };
  });
};

interface IAddEditToolManagementProps extends IFormModalProps {
  selectedToolType?: number;
}

/**
* This functional component renders the Add/Edit Tool Management form, wires up its
* initial values from the store and submits the built payload to the Add/Update APIs.
* @param props: Tool Identifier, modal close handler and the currently selected Tool Type tab
*/
const AddEditToolManagement = (props: IAddEditToolManagementProps) => {
  const { toolManagementStore, factoryStore, processStore } = useStore();
  const {
    AddToolManagementService,
    UpdateToolManagementService,
    GetToolManagementByIdService,
    AddToolTempFilesService,
    resetAddUpdateToolManagementState,
    resetToolManagementDetail,
    addUpdateToolManagementState,
    toolManagementDetails,
  } = toolManagementStore;
  const { allAvailableFactories, GetAllFactoriesService: GetAllFactories } = factoryStore;
  const { allProcessOptionsToolManagement, GetAllProcessService } = processStore;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // For a new tool (props.id < 0), default ToolType to whichever tab was open, and — since
  // Main Unit/Applicator tools are always crimp-related — pre-select InspectionFormat as
  // Crimp Tool Inspection for those two tabs. Editing an existing tool keeps its own values.
  const isMainUnitOrApplicatorTab =
    props.selectedToolType === ToolTypeEnum.MainUnit || props.selectedToolType === ToolTypeEnum.Applicator;
  const initialValues: IToolManagementVM =
    props.id < 0
      ? {
          ...toolManagementDetails,
          ToolType: props.selectedToolType ?? ToolTypeEnum.GeneralTool,
          // Owner Factory always defaults to the logged-in user's own factory when adding a new tool.
          OwnerFactoryId: Number(getFactoryIdFromLocalStorage()) || toolManagementDetails.OwnerFactoryId,
          InspectionFormat: isMainUnitOrApplicatorTab
            ? InspectionFormatEnum.CrimpingToolInspection
            : toolManagementDetails.InspectionFormat,
          // ProcessUsed's multi-select edits an array of { value, label } options, not the
          // raw JSON-names/legacy-name string the API sends.
          ProcessUsed: parseProcessUsedToOptions(
            toolManagementDetails.ProcessUsed,
            allProcessOptionsToolManagement
          ) as unknown as string,
        }
      : {
          ...toolManagementDetails,
          ProcessUsed: parseProcessUsedToOptions(
            toolManagementDetails.ProcessUsed,
            allProcessOptionsToolManagement
          ) as unknown as string,
        };

  /**
   * This useEffect loads the factory/process options and the tool detail (when editing) as
   * soon as the popup opens.
   */
  useEffect(() => {
    // Owner Factory is a cross-factory picker — it must always list every factory, not just
    // the logged-in user's own one.
    GetAllFactories(true);
    GetAllProcessService();
    if (props.id > 0) {
      GetToolManagementByIdService(props.id);
    } else {
      resetToolManagementDetail();
    }
  }, []);

  /**
   * This useEffect shows a success toast and closes the popup once the Add/Update API call
   * succeeds.
   */
  useEffect(() => {
    if (addUpdateToolManagementState.success) {
      if (props.id === -1) toast.success(formatMessage('added_success'));
      else toast.success(formatMessage('updated_success'));
      onModalClosed();
    }
  }, [addUpdateToolManagementState.success]);

  /**
   * This useEffect shows an error toast if the Add/Update API call fails.
   */
  useEffect(() => {
    if (addUpdateToolManagementState.error) {
      toast.error(formatMessage(addUpdateToolManagementState.error));
      resetAddUpdateToolManagementState();
    }
  }, [addUpdateToolManagementState.error]);

  /**
  * This function is used to build the Uci file and checkitem detail arrays for Add, uploading
  * any locally previewed images to the temp files API first.
  * @param tabs: Inspection Tabs
  * @param userId: Logged-in User Identifier
  */
  const buildToolUciDetails = async (
    tabs: IToolInspectionTab[],
    userId: number
  ): Promise<{
    toolUciFileDetails: IToolUciFileDetailRequest[];
    toolUciCheckitemInfoDetails: IToolUciCheckitemInfoDetailRequest[];
    hasFailedImageUpload: boolean;
  }> => {
    const toolUciFileDetails: IToolUciFileDetailRequest[] = [];
    const toolUciCheckitemInfoDetails: IToolUciCheckitemInfoDetailRequest[] = [];
    let hasFailedImageUpload = false;

    for (const tab of tabs) {
      for (let imageIndex = 0; imageIndex < tab.Images.length; imageIndex++) {
        const image = tab.Images[imageIndex];
        let s3Path = image.S3FilePath || image.FilePath || '';
        let fileName = image.FileName || `${tab.Title || 'tab'}-${imageIndex + 1}`;
        let tempId = image.FileId || image.Id || 0;
        let uploadFailed = false;

        if (!s3Path && image.PreviewUrl) {
          if (image.PreviewUrl.startsWith('http://') || image.PreviewUrl.startsWith('https://')) {
            s3Path = image.PreviewUrl;
          } else if (image.PreviewUrl.startsWith('data:')) {
            try {
              const file = await dataUrlToFile(image.PreviewUrl, fileName);
              const uploaded = await AddToolTempFilesService(file, userId);
              if (uploaded) {
                s3Path = uploaded.S3FilePath || uploaded.FilePath || s3Path;
                fileName = uploaded.FileName || fileName;
                tempId = uploaded.Id || tempId;
              } else {
                uploadFailed = true;
              }
            } catch (err) {
              console.error(errorMessage.SomethingWentWrong);
              uploadFailed = true;
            }
          }
        }

        if (uploadFailed) {
          hasFailedImageUpload = true;
          continue;
        }

        if (s3Path || image.PreviewUrl) {
          toolUciFileDetails.push({
            Heading: tab.Title,
            FileName: fileName,
            S3FilePath: s3Path || image.PreviewUrl || '',
            TempId: tempId,
            FileOrder: imageIndex + 1,
            TabIndex: tab.TabIndex,
            TabOrder: tab.TabIndex,
            IsDefault: imageIndex === 0,
            CreatedBy: userId,
          });
        }
      }

      const orderInHeader: Record<string, number> = {};
      flattenTabCheckItems(tab).forEach(({ item, header, headerOrder }) => {
        orderInHeader[header] = (orderInHeader[header] || 0) + 1;
        const text = (item.CheckItemMessage || '').trim();
        if (text) {
          toolUciCheckitemInfoDetails.push({
            CheckHeader: header,
            HeaderName: header,
            HeaderOrder: headerOrder,
            TabIndex: tab.TabIndex,
            CheckitemText: text,
            CheckitemOrder: orderInHeader[header],
            AllowTextEntry: !!item.IsChecked,
          });
        }
      });
    }

    return { toolUciFileDetails, toolUciCheckitemInfoDetails, hasFailedImageUpload };
  };

  /**
  * This function is used to build the Uci file and checkitem detail arrays for Update. It
  * compares the current tabs against the tabs the tool was loaded with and tags each row
  * DBAction 1 (Add), 2 (Update) or 3 (Delete) accordingly. Unchanged rows are skipped.
  * @param currentTabs: Inspection Tabs from the current form values
  * @param initialTabs: Inspection Tabs the tool was loaded with
  * @param toolId: Tool Identifier
  * @param userId: Logged-in User Identifier
  */
  const buildUpdateToolUciDetails = async (
    currentTabs: IToolInspectionTab[],
    initialTabs: IToolInspectionTab[],
    toolId: number,
    userId: number
  ): Promise<{
    updateToolUciFileDetails: IUpdateToolUciFileDetailRequest[];
    updateToolUciCheckitemInfoDetails: IUpdateToolUciCheckitemInfoDetailRequest[];
    hasFailedImageUpload: boolean;
  }> => {
    type IUciFileRow = {
      Heading: string;
      FileName: string;
      S3FilePath: string;
      FileOrder: number;
      TabIndex: number;
      TabOrder: number;
      IsDefault: boolean;
    };
    type IUciCheckItemRow = {
      CheckHeader: string;
      HeaderName: string;
      HeaderOrder: number;
      TabIndex: number;
      CheckitemText: string;
      CheckitemOrder: number;
      AllowTextEntry: boolean;
    };

    // Step 1: snapshot the tabs as they were when the tool was loaded, keyed by saved Id, so
    // the current tabs below can be compared against what's actually in the database.
    const initialImagesById = new Map<number, IUciFileRow>();
    const initialCheckItemsById = new Map<number, IUciCheckItemRow>();
    initialTabs.forEach((tab) => {
      tab.Images.forEach((image, imageIndex) => {
        if (image.Id) {
          initialImagesById.set(image.Id, {
            Heading: tab.Title,
            FileName: image.FileName || '',
            S3FilePath: image.S3FilePath || image.FilePath || '',
            FileOrder: imageIndex + 1,
            TabIndex: tab.TabIndex,
            TabOrder: tab.TabIndex,
            IsDefault: imageIndex === 0,
          });
        }
      });

      const orderInHeader: Record<string, number> = {};
      flattenTabCheckItems(tab).forEach(({ item, header, headerOrder }) => {
        orderInHeader[header] = (orderInHeader[header] || 0) + 1;
        if (item.Id) {
          initialCheckItemsById.set(item.Id, {
            CheckHeader: header,
            HeaderName: header,
            HeaderOrder: headerOrder,
            TabIndex: tab.TabIndex,
            CheckitemText: item.CheckItemMessage,
            CheckitemOrder: orderInHeader[header],
            AllowTextEntry: !!item.IsChecked,
          });
        }
      });
    });

    // These track every Id we come across while walking the current tabs, so that once we're
    // done we can tell which of the originally saved rows are missing (Step 3, deleted by the user).
    const currentImageIds = new Set<number>();
    const currentCheckItemIds = new Set<number>();
    const updateToolUciFileDetails: IUpdateToolUciFileDetailRequest[] = [];
    const updateToolUciCheckitemInfoDetails: IUpdateToolUciCheckitemInfoDetailRequest[] = [];
    let hasFailedImageUpload = false;

    // Step 2: walk the current tabs and decide, row by row, whether it's an Add, an Update or
    // nothing at all (unchanged rows don't need to be sent).
    for (const tab of currentTabs) {
      for (let imageIndex = 0; imageIndex < tab.Images.length; imageIndex++) {
        const image = tab.Images[imageIndex];
        const current: IUciFileRow = {
          Heading: tab.Title,
          FileName: image.FileName || `${tab.Title || 'tab'}-${imageIndex + 1}`,
          S3FilePath: image.S3FilePath || image.FilePath || '',
          FileOrder: imageIndex + 1,
          TabIndex: tab.TabIndex,
          TabOrder: tab.TabIndex,
          IsDefault: imageIndex === 0,
        };

        if (!image.Id) {
          // New image added in this edit, upload it if it's still a local preview.
          let s3Path = current.S3FilePath;
          let fileName = current.FileName;
          let tempId = image.FileId || 0;
          let uploadFailed = false;

          if (!s3Path && image.PreviewUrl) {
            if (image.PreviewUrl.startsWith('http://') || image.PreviewUrl.startsWith('https://')) {
              s3Path = image.PreviewUrl;
            } else if (image.PreviewUrl.startsWith('data:')) {
              try {
                const file = await dataUrlToFile(image.PreviewUrl, fileName);
                const uploaded = await AddToolTempFilesService(file, userId);
                if (uploaded) {
                  s3Path = uploaded.S3FilePath || uploaded.FilePath || s3Path;
                  fileName = uploaded.FileName || fileName;
                  tempId = uploaded.Id || tempId;
                } else {
                  uploadFailed = true;
                }
              } catch (err) {
                console.error(errorMessage.SomethingWentWrong);
                uploadFailed = true;
              }
            }
          }

          if (uploadFailed) {
            hasFailedImageUpload = true;
            continue;
          }

          if (s3Path || image.PreviewUrl) {
            updateToolUciFileDetails.push({
              Id: 0,
              ToolId: toolId,
              ...current,
              S3FilePath: s3Path || image.PreviewUrl || '',
              FileName: fileName,
              TempId: tempId,
              UpdatedBy: userId,
              DBAction: 1,
            });
          }
          continue;
        }

        // Existing image — mark it seen, then compare against the snapshot field by field.
        // Only push an Update row if something actually changed; otherwise leave it out.
        currentImageIds.add(image.Id);
        const initial = initialImagesById.get(image.Id);
        const isChanged =
          !initial ||
          initial.Heading !== current.Heading ||
          initial.FileName !== current.FileName ||
          initial.S3FilePath !== current.S3FilePath ||
          initial.FileOrder !== current.FileOrder ||
          initial.TabIndex !== current.TabIndex ||
          initial.IsDefault !== current.IsDefault;

        if (isChanged) {
          updateToolUciFileDetails.push({
            Id: image.Id,
            ToolId: toolId,
            ...current,
            TempId: 0,
            UpdatedBy: userId,
            DBAction: 2,
          });
        }
      }

      const orderInHeader: Record<string, number> = {};
      flattenTabCheckItems(tab).forEach(({ item, header, headerOrder }) => {
        const text = (item.CheckItemMessage || '').trim();
        if (!text) return;
        orderInHeader[header] = (orderInHeader[header] || 0) + 1;
        const current: IUciCheckItemRow = {
          CheckHeader: header,
          HeaderName: header,
          HeaderOrder: headerOrder,
          TabIndex: tab.TabIndex,
          CheckitemText: text,
          CheckitemOrder: orderInHeader[header],
          AllowTextEntry: !!item.IsChecked,
        };

        if (!item.Id) {
          updateToolUciCheckitemInfoDetails.push({
            Id: 0,
            ToolId: toolId,
            ...current,
            UpdatedBy: userId,
            DBAction: 1,
          });
          return;
        }

        // Same idea as the images above — same Id, only send it if it actually changed.
        currentCheckItemIds.add(item.Id);
        const initial = initialCheckItemsById.get(item.Id);
        const isChanged =
          !initial ||
          initial.CheckHeader !== current.CheckHeader ||
          initial.HeaderOrder !== current.HeaderOrder ||
          initial.TabIndex !== current.TabIndex ||
          initial.CheckitemText !== current.CheckitemText ||
          initial.CheckitemOrder !== current.CheckitemOrder ||
          initial.AllowTextEntry !== current.AllowTextEntry;

        if (isChanged) {
          updateToolUciCheckitemInfoDetails.push({
            Id: item.Id,
            ToolId: toolId,
            ...current,
            UpdatedBy: userId,
            DBAction: 2,
          });
        }
      });
    }

    // Step 3: anything that was in the original snapshot but never got marked "seen" in Step 2
    // was removed by the user — build its Delete row straight from the snapshot, since it's no
    // longer in the current tabs to read from.
    initialImagesById.forEach((initial, id) => {
      if (!currentImageIds.has(id)) {
        updateToolUciFileDetails.push({
          Id: id,
          ToolId: toolId,
          ...initial,
          TempId: 0,
          UpdatedBy: userId,
          DBAction: 3,
        });
      }
    });

    initialCheckItemsById.forEach((initial, id) => {
      if (!currentCheckItemIds.has(id)) {
        updateToolUciCheckitemInfoDetails.push({
          Id: id,
          ToolId: toolId,
          ...initial,
          UpdatedBy: userId,
          DBAction: 3,
        });
      }
    });

    return { updateToolUciFileDetails, updateToolUciCheckitemInfoDetails, hasFailedImageUpload };
  };

  /**
  * This function is used to build the crimp master detail array for Update, the same way
  * buildUpdateToolUciDetails does it for tabs — DBAction 1 (Add) / 2 (Update) / 3 (Delete).
  * @param currentRows: Crimp Rows from the current form values
  * @param initialRows: Crimp Rows the tool was loaded with
  * @param toolId: Tool Identifier
  * @param userId: Logged-in User Identifier
  */
  const buildUpdateToolCrimpMasterDetails = (
    currentRows: IToolCrimpRow[],
    initialRows: IToolCrimpRow[],
    toolId: number,
    userId: number
  ): IUpdateToolCrimpMasterDetailRequest[] => {
    type ICrimpRow = { Awg: string; UpperLimit: number; LowerLimit: number; TensileStrength: number };

    const initialById = new Map<number, ICrimpRow>();
    initialRows.forEach((row) => {
      if (row.Id) {
        initialById.set(row.Id, {
          Awg: row.AWG,
          UpperLimit: Number(row.UpperLimitCH) || 0,
          LowerLimit: Number(row.LowerLimitCH) || 0,
          TensileStrength: Number(row.TensileStrengthLimit) || 0,
        });
      }
    });

    const currentIds = new Set<number>();
    const details: IUpdateToolCrimpMasterDetailRequest[] = [];

    currentRows.forEach((row) => {
      const current: ICrimpRow = {
        Awg: row.AWG,
        UpperLimit: Number(row.UpperLimitCH) || 0,
        LowerLimit: Number(row.LowerLimitCH) || 0,
        TensileStrength: Number(row.TensileStrengthLimit) || 0,
      };

      if (!row.Id) {
        details.push({ Id: 0, ToolId: toolId, ...current, UpdatedBy: userId, DBAction: 1 });
        return;
      }

      currentIds.add(row.Id);
      const initial = initialById.get(row.Id);
      const isChanged =
        !initial ||
        initial.Awg !== current.Awg ||
        initial.UpperLimit !== current.UpperLimit ||
        initial.LowerLimit !== current.LowerLimit ||
        initial.TensileStrength !== current.TensileStrength;

      if (isChanged) {
        details.push({ Id: row.Id, ToolId: toolId, ...current, UpdatedBy: userId, DBAction: 2 });
      }
    });

    initialById.forEach((initial, id) => {
      if (!currentIds.has(id)) {
        details.push({ Id: id, ToolId: toolId, ...initial, UpdatedBy: userId, DBAction: 3 });
      }
    });

    return details;
  };

  /**
  * This function is used to build the Add/Update Tool Management payload from the form values
  * and submit it to the right API based on the tool identifier.
  * @param rawValues: Tool Management Form Values
  */
  const onSubmitForm = async (rawValues: IToolManagementVM) => {
    const userId = Number(getUserId()) || 0;
    const values = mapDateFieldsForSubmit(clearPleaseSelectSentinels(rawValues));
    const { InspectionTabs, CrimpRows } = values;
    const baseFields = pickBaseToolManagementFields(values);
    if (Number(baseFields.UsageStatus) === UsageStatusEnum.Unusable) {
      baseFields.InspectionStatus = InspectionStatusEnum.NotInspected;
    }

    setIsSubmitting(true);
    try {
      const ownerFactoryId = Number(baseFields.OwnerFactoryId);
      // ProcessUsed is edited as an array of { value, label } options — encode it back to
      // ProcessUsed's JSON array format before submitting.
      const processUsed = stringifyProcessUsedNames(baseFields.ProcessUsed as any, allProcessOptionsToolManagement);
      const loanFactoryId = baseFields.LoanFactoryId ? Number(baseFields.LoanFactoryId) : ownerFactoryId;
      // baseFields.InspectionFormat is already number-coerced by pickBaseToolManagementFields —
      // values.InspectionFormat itself is still whatever the native <select> yielded (a string).
      const isUserCreatedInspection = baseFields.InspectionFormat === InspectionFormatEnum.UserCreated;
      // A tool with Inspection Cycle "None" is never inspected, so Step 2 (Crimp rows /
      // User Created check items) is skipped entirely and must never send - or leave behind -
      // any inspection data, even if InspectionFormat/CrimpRows/InspectionTabs still hold
      // stale values from before the cycle was switched to None.
      const isNoneCycle = Number(baseFields.InspectionCycle) === InspectionCycleEnum.None;

      if (props.id < 0) {
        // Crimp rows and UCI tabs are mutually exclusive by InspectionFormat, so whichever one
        // isn't selected goes out blank.
        const toolCrimpMasterDetails: IToolCrimpMasterDetailRequest[] = isNoneCycle || isUserCreatedInspection
          ? []
          : (CrimpRows || []).map((row) => ({
              Awg: row.AWG,
              UpperLimit: Number(row.UpperLimitCH) || 0,
              LowerLimit: Number(row.LowerLimitCH) || 0,
              TensileStrength: Number(row.TensileStrengthLimit) || 0,
            }));

        const { toolUciFileDetails, toolUciCheckitemInfoDetails, hasFailedImageUpload } = !isNoneCycle && isUserCreatedInspection
          ? await buildToolUciDetails(InspectionTabs || [], userId)
          : { toolUciFileDetails: [], toolUciCheckitemInfoDetails: [], hasFailedImageUpload: false };

        if (hasFailedImageUpload) {
          toast.error(formatMessage('image_upload_failed'));
          return;
        }

        AddToolManagementService({
          ...baseFields,
          OwnerFactoryId: ownerFactoryId,
          ProcessUsed: processUsed,
          LoanFactoryId: loanFactoryId,
          CreatedBy: userId,
          ToolUciFileDetails: toolUciFileDetails,
          ToolUciCheckitemInfoDetails: toolUciCheckitemInfoDetails,
          ToolCrimpMasterDetails: toolCrimpMasterDetails,
          TenantId: Number(getTenantIdFromLocalStorage()),
        } as unknown as IAddToolManagement);
      } else {
        // isNoneCycle forces the "current" side of both diffs to empty, regardless of what's
        // still sitting in CrimpRows/InspectionTabs - so switching an existing tool to None
        // generates DBAction=3 (Delete) for every row/tab/check-item it previously had, instead
        // of a no-op diff that would silently leave the old inspection data in place.
        const updateToolCrimpMasterDetails = buildUpdateToolCrimpMasterDetails(
          isNoneCycle ? [] : (CrimpRows || []),
          initialValues.CrimpRows || [],
          props.id,
          userId
        );

        const wasUserCreatedInspection =
          Number(initialValues.InspectionFormat) === InspectionFormatEnum.UserCreated;
        const { updateToolUciFileDetails, updateToolUciCheckitemInfoDetails, hasFailedImageUpload } =
          isNoneCycle || isUserCreatedInspection || wasUserCreatedInspection
            ? await buildUpdateToolUciDetails(
                !isNoneCycle && isUserCreatedInspection ? InspectionTabs || [] : [],
                initialValues.InspectionTabs || [],
                props.id,
                userId
              )
            : { updateToolUciFileDetails: [], updateToolUciCheckitemInfoDetails: [], hasFailedImageUpload: false };

        if (hasFailedImageUpload) {
          toast.error(formatMessage('image_upload_failed'));
          return;
        }

        UpdateToolManagementService({
          ...baseFields,
          ToolId: props.id,
          OwnerFactoryId: ownerFactoryId,
          ProcessUsed: processUsed,
          LoanFactoryId: loanFactoryId,
          UpdatedBy: userId,
          // Always true on update
          HasInspectionInfo: true,
          UpdateToolUciFileDetails: updateToolUciFileDetails,
          UpdateToolUciCheckitemInfoDetails: updateToolUciCheckitemInfoDetails,
          UpdateToolCrimpMasterDetails: updateToolCrimpMasterDetails,
          TenantId: Number(getTenantIdFromLocalStorage()),
        } as unknown as IUpdateToolManagement);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onModalClosed = () => {
    resetToolManagementDetail();
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <AddEditToolManagementForm
        id={props.id}
        initialValues={initialValues}
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        isLoading={
          isSubmitting ||
          addUpdateToolManagementState.inProgress ||
          toolManagementStore.toolManagementState.inProgress
        }
        factoryOptions={allAvailableFactories}
        processOptions={allProcessOptionsToolManagement}
      />
    </React.Fragment>
  );
};

export default observer(AddEditToolManagement);
